import { usePhoneMode, useIsSinglePage } from "../../../hooks";
import { HorizontalListItem } from "../../HorizontalList";
import { Button, Menu, MenuItem } from "@mui/material";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUsePageNumbering } from "../../../redux/documentViewSlice";

export default function DocumentViewMenuPageMarker({ currentPage, pageCount, hasCover, onShowJumpModal, pageMappings }) {
    const { isMobile, isLandscape } = usePhoneMode();
    const { isSinglePageMode } = useIsSinglePage();
    const dispatch = useDispatch();
    const usePageNumbering = useSelector((state) => state.documentView.usePageNumbering);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelectPageNumbering = () => {
        dispatch(setUsePageNumbering(true));
        handleClose();
    };

    const handleSelectManualNumbering = () => {
        dispatch(setUsePageNumbering(false));
        handleClose();
    };

    const hasAnyPageText = useMemo(() => {
        return pageMappings?.some(mapping => !!mapping.pageNumberText && mapping.pageNumberText != mapping.pageNumber && mapping.pageNumberText.match(/[0-9]+[A-Za-z]-[0-9]+/g));
    }, [pageMappings]);

    const currentPageMapping = pageMappings?.find(mapping => mapping.pageNumber == (!hasCover ? currentPage + 1 : currentPage));
    const nextPageMapping = pageCount >= currentPage + 1 ? pageMappings?.find(mapping => mapping.pageNumber == (!hasCover ? currentPage + 2 : currentPage + 1)) : null;

    let ofCount = ` of ${pageCount}`;
    let currentTextDisplay = hasCover ? currentPage.toString() : (currentPage + 1).toString();
    if (hasAnyPageText && !usePageNumbering && !!currentPageMapping?.pageNumberText) {
        currentTextDisplay = currentPageMapping.pageNumberText;
        ofCount = '';
    }

    let nextTextDisplay = hasCover ? (currentPage + 1).toString() : (currentPage + 2).toString();
    if (hasAnyPageText && !usePageNumbering && !!nextPageMapping?.pageNumberText) {
        nextTextDisplay = nextPageMapping.pageNumberText;
        ofCount = '';
    }

    const pageNumberMenu = !isMobile && hasAnyPageText ? (
        <>
            <Button
                style={{ textTransform: 'unset', padding: '6px 0', minWidth: 'unset' }}
                id="demo-positioned-button"
                aria-controls={open ? 'demo-positioned-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
            >
                <ArrowDropUpIcon />
            </Button>
            <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <MenuItem onClick={handleSelectPageNumbering} selected={usePageNumbering}>Page Numbers (e.g. 1, 2, 3)</MenuItem>
                <MenuItem onClick={handleSelectManualNumbering} selected={!usePageNumbering}>Manual Numbers (e.g. 1A-1, 1A-2, 1A-3)</MenuItem>
            </Menu>
        </>
    ) : null;

    if (isMobile && !isLandscape && hasCover) {
        if (currentPage < 0) {
            return (
                <JumpToButton
                    onClick={onShowJumpModal}
                >
                    Cover
                </JumpToButton>
            );
        } else if (currentPage === 0) {
            return (
                <JumpToButton
                    onClick={onShowJumpModal}
                >
                    TOC
                </JumpToButton>
            );
        } else {
            return (
                <JumpToButton
                    onClick={onShowJumpModal}
                >
                    Page {currentPage}
                </JumpToButton>
            );
        }
    } else if (isMobile && !isLandscape && !hasCover) {
        return (
            <JumpToButton
                onClick={onShowJumpModal}
            >
                Page {currentPage}
            </JumpToButton>
        );
    }

    if (hasCover && currentPage === 0) {
        return (
            <>
                <JumpToButton
                    onClick={onShowJumpModal}
                >
                    TOC
                </JumpToButton>
                {pageNumberMenu}
            </>
        );
    }

    if (hasCover && currentPage < 0) {
        return (
            <>
                <JumpToButton
                    onClick={onShowJumpModal}
                >
                    Cover of {pageCount}
                </JumpToButton>
                {pageNumberMenu}
            </>
        );
    }

    if (!hasCover && currentPage == 0) {
        return (
            <>
                <JumpToButton
                    onClick={onShowJumpModal}
                >
                    Page {currentTextDisplay}{ofCount}
                </JumpToButton>
                {pageNumberMenu}
            </>
        );
    }

    return (
        <>
            {pageCount >= currentPage + 1 ? (
                <JumpToButton
                    onClick={onShowJumpModal}
                >
                    Page {isSinglePageMode ? currentTextDisplay : `${currentTextDisplay} - ${nextTextDisplay}`}{ofCount}
                </JumpToButton>
            ) : (
                <JumpToButton
                    onClick={onShowJumpModal}
                >
                    Page {currentTextDisplay}{ofCount}
                </JumpToButton>
            )}
            {pageNumberMenu}
        </>
    );
}

function JumpToButton({ onClick, children }) {
    return (
        <HorizontalListItem>
            <Button
                style={{ textTransform: 'unset' }}
                onClick={onClick}
                title='Jump to Page'
            >
                {children}
            </Button>
        </HorizontalListItem>
    );
}